import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Clock, Users, Flame, ChefHat, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { nutritionApi } from '../services/api';

const RecipeDetailPage = () => {
  const { recipeId } = useParams();
  const { language } = useLanguage();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecipe();
  }, [recipeId]);

  const fetchRecipe = async () => {
    try {
      const data = await nutritionApi.getRecipe(recipeId);
      setRecipe(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <p className="text-gray-400">
          {language === 'en' ? 'Recipe not found' : 'Receta no encontrada'}
        </p>
      </div>
    );
  }

  const ingredients = language === 'en' ? recipe.ingredients_en : recipe.ingredients_es;
  const instructions = language === 'en' ? recipe.instructions_en : recipe.instructions_es;

  return (
    <div className="min-h-screen bg-[#0d1f1f]">
      {/* Hero Image */}
      <div className="relative h-72">
        {recipe.image_url ? (
          <img 
            src={recipe.image_url} 
            alt={recipe.title_en}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-orange-900 to-red-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f1f] to-transparent" />
        
        <Link
          to="/nutrition"
          className="absolute top-4 left-4 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
      </div>

      {/* Content */}
      <div className="px-4 -mt-20 relative z-10 pb-8">
        <div className="bg-gray-900/80 backdrop-blur-sm rounded-3xl p-6 border border-gray-800">
          {/* Tags */}
          <div className="flex gap-2 mb-4">
            <span className="bg-orange-500/20 text-orange-400 text-xs px-3 py-1 rounded-full capitalize">
              {recipe.category}
            </span>
            <span className="bg-gray-800 text-gray-400 text-xs px-3 py-1 rounded-full capitalize">
              {recipe.difficulty}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">
            {language === 'en' ? recipe.title_en : recipe.title_es}
          </h1>

          {/* Info Row */}
          <div className="flex items-center gap-4 text-gray-400 text-sm mb-4">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {recipe.prep_time_minutes + recipe.cook_time_minutes} min
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {recipe.servings} {language === 'en' ? 'servings' : 'porciones'}
            </span>
          </div>

          {/* Description */}
          <p className="text-gray-300 mb-6">
            {language === 'en' ? recipe.description_en : recipe.description_es}
          </p>

          {/* Macros */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="bg-gray-800/50 rounded-xl p-3 text-center">
              <Flame className="w-5 h-5 text-orange-400 mx-auto mb-1" />
              <p className="text-white font-bold">{recipe.calories}</p>
              <p className="text-gray-500 text-xs">kcal</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-3 text-center">
              <p className="text-blue-400 font-bold text-lg">{recipe.protein}g</p>
              <p className="text-gray-500 text-xs">{language === 'en' ? 'Protein' : 'Proteína'}</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-3 text-center">
              <p className="text-yellow-400 font-bold text-lg">{recipe.carbs}g</p>
              <p className="text-gray-500 text-xs">{language === 'en' ? 'Carbs' : 'Carbos'}</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-3 text-center">
              <p className="text-pink-400 font-bold text-lg">{recipe.fat}g</p>
              <p className="text-gray-500 text-xs">{language === 'en' ? 'Fat' : 'Grasa'}</p>
            </div>
          </div>
        </div>

        {/* Ingredients */}
        <div className="mt-6 bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <ChefHat className="w-5 h-5 text-orange-400" />
            {language === 'en' ? 'Ingredients' : 'Ingredientes'}
          </h2>
          <ul className="space-y-3">
            {ingredients.map((ingredient, idx) => (
              <li key={idx} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full border-2 border-orange-500/50 flex items-center justify-center">
                  <Check className="w-3 h-3 text-orange-400" />
                </div>
                <span className="text-gray-300">{ingredient}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
          <h2 className="text-lg font-bold text-white mb-4">
            {language === 'en' ? 'Instructions' : 'Instrucciones'}
          </h2>
          <ol className="space-y-4">
            {instructions.map((step, idx) => (
              <li key={idx} className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold flex-shrink-0">
                  {idx + 1}
                </div>
                <p className="text-gray-300 pt-1">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default RecipeDetailPage;
